<template>
  <div class="apikey-page">
    <div class="toolbar">
      <el-button type="primary" @click="openDialog">新增 ApiKey</el-button>
    </div>
    <el-table :data="list" stripe style="width:100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="appid" label="AppID" width="160" />
      <el-table-column label="ApiKey" width="260">
        <template #default="{row}">
          <span>{{ maskKey(row.apikey) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="有效期" width="260">
        <template #default="{row}">
          <div>{{ formatTime(row.valid_from) }} ~ {{ formatTime(row.valid_to) }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template #default="{row}">
          <el-switch v-model="row.status" :active-value="1" :inactive-value="0" @change="toggleStatus(row)" />
        </template>
      </el-table-column>
      <el-table-column prop="request_count" label="请求次数" width="120" />
      <el-table-column label="最近使用" width="180">
        <template #default="{row}">
          <span>{{ row.last_used_at ? formatTime(row.last_used_at) : '—' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="180">
        <template #default="{row}">
          <el-button size="small" @click="edit(row)">编辑</el-button>
          <el-button size="small" type="danger" @click="remove(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination class="pager" :current-page="pagination.page" :page-size="pagination.pageSize" :total="pagination.total" layout="prev, pager, next" @current-change="pageChange" />

    <el-dialog v-model="dialogVisible" :title="form.id ? '编辑 ApiKey' : '新增 ApiKey'" width="600px">
      <el-form :model="form" label-width="140px">
        <el-form-item label="AppID">
          <el-input v-model="form.appid" placeholder="请输入 AppID" />
        </el-form-item>
        <el-form-item label="ApiKey">
          <el-input v-model="form.apikey" placeholder="请输入 ApiKey" show-password />
        </el-form-item>
        <el-form-item label="有效期开始">
          <el-date-picker v-model="form.valid_from_ms" type="datetime" value-format="x" placeholder="选择开始时间" />
        </el-form-item>
        <el-form-item label="有效期结束">
          <el-date-picker v-model="form.valid_to_ms" type="datetime" value-format="x" placeholder="选择结束时间" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible=false">取消</el-button>
        <el-button type="primary" :loading="loading" @click="submit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '../../utils/request'

const list = ref([])
const pagination = ref({ page: 1, pageSize: 20, total: 0 })
const dialogVisible = ref(false)
const loading = ref(false)
const form = ref({
  id: null,
  appid: '',
  apikey: '',
  valid_from_ms: null,
  valid_to_ms: null,
  status: 1
})

const formatTime = (ts) => {
  if (!ts) return '—'
  const d = new Date(Number(ts) * 1000)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const da = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${da} ${hh}:${mm}`
}

const maskKey = (k) => {
  if (!k) return '—'
  const s = String(k)
  if (s.length <= 8) return s
  return s.slice(0, 4) + '****' + s.slice(-4)
}

const loadList = async () => {
  const params = { page: pagination.value.page, pageSize: pagination.value.pageSize }
  try {
    const data = await request.get('/v1/admin/apikey/list', { params })
    list.value = (data.list || []).map(it => ({
      ...it,
      status: Number(it.status || 0),
      valid_from: it.valid_from ? Number(it.valid_from) : null,
      valid_to: it.valid_to ? Number(it.valid_to) : null
    }))
    pagination.value.total = data.pagination?.total || list.value.length
  } catch (e) {
    ElMessage.error(e.toString())
  }
}

const pageChange = (p) => { pagination.value.page = p; loadList() }

const openDialog = () => {
  form.value = { id: null, appid: '', apikey: '', valid_from_ms: null, valid_to_ms: null, status: 1 }
  dialogVisible.value = true
}

const edit = (row) => {
  form.value = {
    id: row.id,
    appid: row.appid || '',
    apikey: row.apikey || '',
    valid_from_ms: row.valid_from ? Number(row.valid_from) * 1000 : null,
    valid_to_ms: row.valid_to ? Number(row.valid_to) * 1000 : null,
    status: Number(row.status || 0)
  }
  dialogVisible.value = true
}

const submit = async () => {
  if (!form.value.appid || !form.value.apikey) {
    ElMessage.warning('请填写 AppID 与 ApiKey')
    return
  }
  loading.value = true
  try {
    const payload = {
      id: form.value.id,
      appid: form.value.appid,
      apikey: form.value.apikey,
      valid_from: form.value.valid_from_ms ? Math.floor(form.value.valid_from_ms / 1000) : null,
      valid_to: form.value.valid_to_ms ? Math.floor(form.value.valid_to_ms / 1000) : null,
      status: form.value.status
    }
    if (form.value.id) {
      await request.post('/v1/admin/apikey/update', payload)
    } else {
      await request.post('/v1/admin/apikey/create', payload)
    }
    ElMessage.success('操作成功')
    dialogVisible.value = false
    loadList()
  } catch (e) {
    ElMessage.error('保存失败：' + e.toString())
  } finally {
    loading.value = false
  }
}

const remove = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除这个 ApiKey 吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await request.post('/v1/admin/apikey/delete', { id: row.id })
    ElMessage.success('删除成功')
    loadList()
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.toString())
    }
  }
}

const toggleStatus = async (row) => {
  try {
    await request.post('/v1/admin/apikey/update', { id: row.id, status: row.status })
    ElMessage.success('状态已更新')
  } catch (e) {
    ElMessage.error('更新失败：' + e.toString())
    row.status = row.status ? 0 : 1
  }
}

loadList()
</script>

<style scoped>
.apikey-page .toolbar { margin-bottom: 15px; }
.pager { margin-top: 15px; text-align: right; }
</style>
